import ConfirmDialog from 'components/Modal/ConfirmDialog';
import React, { MutableRefObject, useEffect, useRef, useState } from 'react';
import styled from 'styled-components';

const Wrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
  width: 100%;
`;

const Container = styled.textarea`
  padding: 12px;
  width: 100%;
  color: var(--gray-gray-09, #1b1c1e);

  border-radius: 8px;
  border: 1px solid var(--gray-gray-04, #dbdcdf);
  background: var(--Static-White, #fff);

  /* Body/B-16-Regular */
  font-family: Pretendard;
  font-size: 14px;
  font-style: normal;
  font-weight: 400;
  line-height: 22px; /* 137.5% */
  letter-spacing: -0.6px;
  resize: none;

  &::placeholder {
    color: #c2c4c8;
  }

  &::-webkit-scrollbar {
    display: none; /* Chrome, Safari, Opera*/
  }

  &:focus {
    border: 1.5px solid #1b1c1e;
  }
`;

const Counter = styled.span<{ $isLimitReached: boolean; $isNearLimit: boolean }>`
  align-self: flex-end;
  font-family: Pretendard;
  font-size: 12px;
  font-weight: 400;
  line-height: 16px;
  letter-spacing: -0.4px;
  color: ${props =>
    props.$isLimitReached
      ? '#ef4444'
      : props.$isNearLimit
      ? '#70737c'
      : '#989ba2'};

  & > b {
    font-weight: 600;
    color: ${props => (props.$isLimitReached ? '#ef4444' : '#46474c')};
  }
`;

interface TextareaIProps {
  target: string;
  value: string;
  placeholder: string;
  maxLength?: number;
  isFocusInput?: boolean;
  hideCounter?: boolean;
  style?: React.CSSProperties;
  onChange: (key: string, value: any) => void;
  onFocus?: () => void;
}

const Textarea: React.FC<TextareaIProps> = props => {
  const {
    target,
    value,
    placeholder,
    maxLength,
    isFocusInput,
    hideCounter,
    style,
    onChange,
    onFocus,
  } = props;

  const [localValue, setLocalValue] = useState<string>(value);
  const [isOpenOptionDialog, setIsOpenOptionDialog] = useState<boolean>(false);
  const isComposingRef = useRef<boolean>(false);

  const textareaRef = useRef<HTMLTextAreaElement>(null);

  const handleAdjustTextareaHeight = () => {
    const textarea = textareaRef.current;

    if (textarea) {
      textarea.style.height = 'auto';
      textarea.style.height = `${textarea.scrollHeight}px`;
    }
  };

  useEffect(() => {
    if (!isComposingRef.current) {
      setLocalValue(value);
    }
  }, [value]);

  useEffect(() => {
    handleAdjustTextareaHeight();
  }, [localValue]);

  const handleChange = (e: any) => {
    const newValue = e.target.value;

    setLocalValue(newValue);
    handleAdjustTextareaHeight();

    if (!isComposingRef.current) {
      onChange(target, newValue);
    }
  };

  const handleCompositionStart = () => {
    isComposingRef.current = true;
  };

  const handleCompositionEnd = (e: any) => {
    isComposingRef.current = false;
    onChange(target, e.target.value);
  };

  const currentLength = localValue?.length ?? 0;
  const isLimitReached = !!maxLength && currentLength >= maxLength;
  const isNearLimit =
    !!maxLength && currentLength >= Math.floor(maxLength * 0.9);

  return (
    <React.Fragment>
      <Wrapper>
        <Container
          placeholder={placeholder}
          ref={textareaRef}
          value={localValue}
          onChange={handleChange}
          onCompositionStart={handleCompositionStart}
          onCompositionEnd={handleCompositionEnd}
          rows={1}
          maxLength={maxLength && maxLength}
          onFocus={() => {
            isFocusInput && setIsOpenOptionDialog(true);
          }}
          style={{ ...style }}
        />
        {maxLength && !hideCounter && (
          <Counter
            $isLimitReached={isLimitReached}
            $isNearLimit={isNearLimit}
            aria-live="polite"
          >
            <b>{currentLength}</b>
            {` / ${maxLength}`}
          </Counter>
        )}
      </Wrapper>
      {isOpenOptionDialog && (
        <ConfirmDialog
          title="옵션 타이틀을 변경 할까요?"
          description="옵션 타이틀을 변경하면, 해당 옵션뿐만 아니라 다른 옵션의 타이틀도 함께 변경돼요."
          onCloseText="아니요"
          onClose={() => setIsOpenOptionDialog(false)}
          onAcceptText="네, 변경할게요"
          onAccept={() => {
            textareaRef.current.focus();
            setIsOpenOptionDialog(false);
          }}
        />
      )}
    </React.Fragment>
  );
};

export default Textarea;
